import { useEffect, useRef, useState } from 'react';
import { IconClockHour4, IconTrophy } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { ScrollArea } from '../ui/scroll-area';
import { CARD, ContestAvatar, RankMove, locationOf, useNumFmt } from './contestUi';

// Contest leaderboard: top-3 podium, the ranked list with the winners cut-off line, a "Top 50 (the
// winners) / All 250" switch, and the signed-in owner's own row pinned below the list when it isn't in
// it. The list has a max height and scrolls inside the card: the top 50 are right there, everybody
// after them follows by scrolling. Rows come from the public leaderboard (the user id is never exposed),
// so the owner's own rank/points come from /api/contest/me (matched by user id on the server):
// `me.rank` is null when they are outside the board.
// NB: the global `p { font-weight: 400 !important }` strips bold from <p> — bold text uses span/div.

// Max height of the scrolling list (Radix ScrollArea: the cap goes on its viewport; its inner wrapper
// must stay a block, otherwise long names stop truncating).
const LIST_SCROLL = '[&_[data-radix-scroll-area-viewport]]:max-h-[640px] [&_[data-radix-scroll-area-viewport]>div]:!block';

// Rank number colour of the first three rows: gold / silver / bronze.
const RANK_INK = ['text-amber-600 dark:text-amber-400', 'text-zinc-400', 'text-amber-800 dark:text-amber-600'];
// Podium order is [2nd, 1st, 3rd]; index = place - 1.
const PODIUM = [
    { ring: 'border-amber-500', pill: 'bg-amber-500', base: 'bg-amber-50 dark:bg-amber-900/20', avatar: 'h-16 w-16', height: 'h-[92px]' },
    { ring: 'border-zinc-400', pill: 'bg-zinc-400', base: 'bg-zinc-100 dark:bg-zinc-800', avatar: 'h-[52px] w-[52px]', height: 'h-[70px]' },
    { ring: 'border-amber-700', pill: 'bg-amber-700', base: 'bg-orange-100/70 dark:bg-orange-900/20', avatar: 'h-[52px] w-[52px]', height: 'h-[58px]' },
];
const ROW_GRID = 'grid grid-cols-[40px_1fr_auto] items-center gap-3 px-[22px]';
const round2 = (n) => Math.max(1, Math.round(n * 100) / 100);

function Tab({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`h-[34px] rounded-[9px] px-[13px] text-sm font-semibold transition-colors ${active ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100' : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'}`}
        >
            {children}
        </button>
    );
}

export default function ContestTopList({
    leaderboard = [], me, phase = 'live', winnerCount = 50, showPodium = true, currentUserName, currentUserAvatar,
}) {
    const { t } = useTranslation();
    const fmt = useNumFmt();

    const [tab, setTab] = useState('win');         // 'win' = the winners (top N) · 'all' = everyone ranked
    const [flash, setFlash] = useState(null);          // row briefly highlighted by "jump to my row"
    const scrollRef = useRef(null);
    const rowRefs = useRef({});
    const timers = useRef([]);
    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const live = phase === 'live';
    const rows = leaderboard;                          // everyone ranked (the board holds up to 250)
    const inBoard = me?.rank != null;
    const myPts = me?.points?.total ?? 0;
    // The board is a snapshot that refreshes once an hour, but the owner's OWN points come live from /me —
    // so their entry shows the same number as the card at the top (their rank still moves hourly).
    const pointsOf = (e, mine) => (live && mine ? myPts : e.point);

    // The two tabs only differ while more people are ranked than there are winners.
    const hasTabs = rows.length > winnerCount;
    const showAll = hasTabs && tab === 'all';
    const tabTotal = showAll ? rows.length : Math.min(winnerCount, rows.length);

    // Which rows are listed (the podium shows the first three).
    const usePodium = showPodium && rows.length >= 3;
    const startAt = usePodium ? 3 : 0;
    const visible = rows.slice(startAt, Math.max(startAt, tabTotal));
    const scroller = () => scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    const changeTab = (next) => { setTab(next); scroller()?.scrollTo({ top: 0 }); };

    // The owner's own row: highlighted in the list, or pinned below it when the list doesn't contain it.
    const myVisible = inBoard && ((usePodium && me.rank <= 3) || (me.rank > startAt && me.rank <= tabTotal));
    const pinMe = !!me && (live || phase === 'completed') && rows.length > 0 && !myVisible;
    const cut = leaderboard[winnerCount - 1];
    const pinNote = phase === 'completed'
        ? t(inBoard ? 'contest.page.finalPosition' : 'contest.page.notInFinal')
        : cut && myPts <= cut.point
            ? t('contest.page.pinGap', { n: fmt(round2(cut.point - myPts + 1)) })
            : t(inBoard ? 'contest.leaderboard.yourPosition' : 'contest.page.outOfList');

    const jumpToMe = () => {
        if (!inBoard || me.rank > rows.length) return;
        if (hasTabs && me.rank > winnerCount) setTab('all'); // the winners tab can't show them
        setFlash(me.rank);
        timers.current.push(setTimeout(() => {
            const row = rowRefs.current[me.rank];
            const box = scroller();
            if (row && box) box.scrollTo({ top: Math.max(0, row.offsetTop - box.clientHeight / 2 + row.clientHeight / 2), behavior: 'smooth' });
        }, 80));
        timers.current.push(setTimeout(() => setFlash(null), 1800));
    };

    return (
        <section className={`${CARD} flex min-w-0 flex-col overflow-hidden`}>
            <div className="flex flex-col gap-3.5 px-[22px] pb-3.5 pt-[22px]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-col gap-0.5">
                        <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('contest.leaderboard.heading')}</div>
                        {live && (
                            <div className="flex items-center gap-1.5 text-[13px] text-zinc-500 dark:text-zinc-400">
                                <span className="h-[7px] w-[7px] rounded-full bg-emerald-500" />{t('contest.leaderboard.updatesHourly')}
                            </div>
                        )}
                    </div>
                    {rows.length > 0 && phase !== 'pending' && hasTabs && (
                        <div className="flex gap-0.5 rounded-[11px] bg-zinc-100 p-[3px] dark:bg-zinc-800">
                            <Tab active={!showAll} onClick={() => changeTab('win')}>{t('contest.leaderboard.tabTop', { n: fmt(winnerCount) })}</Tab>
                            <Tab active={showAll} onClick={() => changeTab('all')}>{t('contest.leaderboard.tabAll', { n: fmt(rows.length) })}</Tab>
                        </div>
                    )}
                </div>

                {/* Podium — top 3 */}
                {usePodium && phase !== 'pending' && (
                    <div className="grid grid-cols-[1fr_1.1fr_1fr] items-end gap-2 pt-1.5">
                        {[1, 0, 2].map((i) => {
                            const e = rows[i];
                            const s = PODIUM[i];
                            const mine = inBoard && me.rank === e.rank;
                            return (
                                <div key={e.rank} className="flex min-w-0 flex-col items-center gap-1.5">
                                    <div className="relative">
                                        <ContestAvatar src={e.avatar} name={e.name} className={`${s.avatar} border-[3px] text-lg ${s.ring}`} />
                                        <span className={`absolute -bottom-1.5 left-1/2 flex h-6 min-w-[24px] -translate-x-1/2 items-center justify-center rounded-full border-2 border-white px-1 text-[13px] font-bold text-white dark:border-zinc-900 ${s.pill}`}>{fmt(e.rank)}</span>
                                    </div>
                                    <div className="mt-1 max-w-full truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{e.name || '—'}{mine && ` (${t('contest.page.youTag')})`}</div>
                                    <div className={`flex w-full flex-col items-center rounded-b-[4px] rounded-t-xl pt-2 ${s.base} ${s.height}`}>
                                        <div className="font-baloo text-lg font-bold leading-[1.1] text-zinc-900 dark:text-zinc-100">{fmt(pointsOf(e, mine))}</div>
                                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('contest.leaderboard.points')}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {phase === 'pending' ? (
                <div className="mx-[22px] mb-[22px] flex flex-col items-center gap-2 rounded-2xl border border-zinc-200 px-4 py-10 text-center dark:border-zinc-700">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary"><IconClockHour4 size={22} /></span>
                    <span className="block text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('contest.leaderboard.pendingMessage')}</span>
                </div>
            ) : rows.length === 0 ? (
                <div className="mx-[22px] mb-[22px] flex flex-col items-center gap-2 rounded-2xl border border-dashed border-zinc-200 px-4 py-10 text-center dark:border-zinc-700">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-800"><IconTrophy size={22} /></span>
                    <span className="block text-sm text-zinc-400 dark:text-zinc-500">{t('contest.page.noStandings')}</span>
                </div>
            ) : (
                <>
                    <div className={`${ROW_GRID} border-y border-zinc-100 bg-zinc-50 py-2 text-xs text-zinc-400 dark:border-zinc-800 dark:bg-zinc-800/40 dark:text-zinc-500`}>
                        <span>{t('contest.leaderboard.rankShort')}</span>
                        <span>{t('contest.leaderboard.participant')}</span>
                        <span>{t('contest.leaderboard.points')}</span>
                    </div>

                    <ScrollArea ref={scrollRef} type="always" className={LIST_SCROLL}>
                    <ol className="flex flex-col">
                        {visible.map((e) => {
                            const mine = inBoard && me.rank === e.rank;
                            const move = (e.prevRank ?? e.rank) - e.rank; // + = moved up
                            return (
                                <li key={e.rank}>
                                    {showAll && e.rank === winnerCount + 1 && (
                                        <div aria-hidden className="flex items-center gap-2.5 bg-zinc-50 px-[22px] py-2 text-[13px] font-semibold text-amber-700 dark:bg-zinc-800/40 dark:text-amber-400">
                                            <span className="h-px flex-1 border-t border-dashed border-amber-300 dark:border-amber-700" />
                                            {t('contest.leaderboard.winnersCut', { n: fmt(winnerCount) })}
                                            <span className="h-px flex-1 border-t border-dashed border-amber-300 dark:border-amber-700" />
                                        </div>
                                    )}
                                    <div
                                        ref={(el) => { rowRefs.current[e.rank] = el; }}
                                        className={`${ROW_GRID} border-b border-l-[3px] border-b-zinc-100 py-2.5 dark:border-b-zinc-800 ${mine ? 'border-l-primary bg-primary/10' : 'border-l-transparent'} ${flash === e.rank ? 'ring-2 ring-inset ring-primary/60' : ''}`}
                                    >
                                        <div className={`font-baloo text-base font-bold ${e.rank <= 3 ? RANK_INK[e.rank - 1] : 'text-zinc-500 dark:text-zinc-400'}`}>{fmt(e.rank)}</div>
                                        <div className="flex min-w-0 items-center gap-[11px]">
                                            <ContestAvatar src={e.avatar} name={e.name} />
                                            <div className="flex min-w-0 flex-col">
                                                <div className="flex min-w-0 items-center gap-[7px]">
                                                    <span className="truncate text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{e.name || '—'}</span>
                                                    {mine && <span className="flex-none rounded-full bg-primary px-2 text-xs font-bold text-white">{t('contest.page.youTag')}</span>}
                                                </div>
                                                {locationOf(e) && <span className="truncate text-[13px] text-zinc-400 dark:text-zinc-500">{locationOf(e)}</span>}
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-0.5">
                                            <span className="font-baloo text-base font-bold text-zinc-900 dark:text-zinc-100">{fmt(pointsOf(e, mine))}</span>
                                            <RankMove move={move} fmt={fmt} />
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                    </ScrollArea>

                    {/* My row, when the list doesn't contain it */}
                    {pinMe && (
                        <button type="button" onClick={jumpToMe} disabled={!inBoard || me.rank > rows.length} className={`${ROW_GRID} w-full border-t border-primary/20 bg-primary/10 py-3 text-left disabled:cursor-default dark:bg-zinc-900`}>
                            <div className="font-baloo text-base font-bold text-primary">{inBoard ? fmt(me.rank) : '—'}</div>
                            <div className="flex min-w-0 items-center gap-[11px]">
                                <ContestAvatar src={currentUserAvatar} name={currentUserName} tint="bg-primary/15 text-primary" />
                                <div className="flex min-w-0 flex-col">
                                    <div className="flex min-w-0 items-center gap-[7px]">
                                        <span className="truncate text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{currentUserName || '—'}</span>
                                        <span className="flex-none rounded-full bg-primary px-2 text-xs font-bold text-white">{t('contest.page.youTag')}</span>
                                    </div>
                                    {/* wraps instead of truncating: "… আরও ২৯ পয়েন্ট দরকার" is long, and a cut-off hides the number's unit */}
                                    <span className="text-[13px] leading-snug text-amber-700 dark:text-amber-400">{pinNote}</span>
                                </div>
                            </div>
                            <div className="font-baloo text-base font-bold text-zinc-900 dark:text-zinc-100">{fmt(myPts)}</div>
                        </button>
                    )}
                </>
            )}
        </section>
    );
}
